import { useState } from "react";
import {
  X,
  Download,
  Share2,
  Printer,
  ZoomIn,
  ZoomOut,
  ChevronLeft,
  ChevronRight,
  FileText,
  Check,
  AlertTriangle,
  Calendar,
  User,
  RotateCcw,
} from "lucide-react";

interface DocumentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onShare?: () => void;
  document?: {
    id: string;
    name: string;
    category: string;
    status: "Signed" | "Completed" | "Draft";
    dateUploaded: string;
    url?: string;
    signedBy?: string;
    signedDate?: string;
    pages?: number;
  };
}

export default function DocumentPreviewModal({
  isOpen,
  onClose,
  onShare,
  document,
}: DocumentPreviewModalProps) {
  const [zoom, setZoom] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = document?.pages || 1;

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 25, 200));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 25, 50));
  const handleResetZoom = () => setZoom(100);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // In a real app, this would trigger a file download
    console.log("Downloading document:", document?.name);
  };

  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  const getStatusStyles = (status: string) => {
    switch (status) {
      case "Signed":
      case "Completed":
        return {
          bg: "bg-[#e6f7ed]",
          text: "text-[#0d7a3e]",
          border: "border-[#b8e6cd]",
          icon: Check,
        };
      case "Draft":
        return {
          bg: "bg-amber-50",
          text: "text-amber-600",
          border: "border-amber-200",
          icon: AlertTriangle,
        };
      default:
        return {
          bg: "bg-gray-100",
          text: "text-gray-600",
          border: "border-gray-200",
          icon: FileText,
        };
    }
  };

  if (!isOpen || !document) return null;

  const statusStyles = getStatusStyles(document.status);
  const StatusIcon = statusStyles.icon;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden border border-[#e8e0f0]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#f0ebf5] bg-[#faf8fc]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#f0ebf5] flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#704aaf]" />
            </div>
            <div>
              <h2 className="text-base font-bold text-[#2d2d2d]">{document.name}</h2>
              <p className="text-xs text-[#6b6b6b]">{document.category}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Status Badge */}
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusStyles.bg} ${statusStyles.text} border ${statusStyles.border}`}>
              <StatusIcon className="w-3 h-3" />
              {document.status}
            </span>
            
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-[#f0ebf5] rounded-lg transition-colors text-[#6b6b6b] hover:text-[#3d3d3d]"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-2 border-b border-[#f0ebf5]">
          <div className="flex items-center gap-2">
            {/* Zoom Controls */}
            <div className="flex items-center gap-1 bg-[#f8f5fc] rounded-lg p-1">
              <button
                onClick={handleZoomOut}
                disabled={zoom <= 50}
                className="p-1.5 rounded hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Zoom Out"
              >
                <ZoomOut className="w-4 h-4 text-[#6b6b6b]" />
              </button>
              <span className="px-2 text-xs font-medium text-[#6b6b6b] min-w-[40px] text-center">
                {zoom}%
              </span>
              <button
                onClick={handleZoomIn}
                disabled={zoom >= 200}
                className="p-1.5 rounded hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Zoom In"
              >
                <ZoomIn className="w-4 h-4 text-[#6b6b6b]" />
              </button>
              <button
                onClick={handleResetZoom}
                className="p-1.5 rounded hover:bg-white transition-colors"
                title="Reset Zoom"
              >
                <RotateCcw className="w-4 h-4 text-[#6b6b6b]" />
              </button>
            </div>

            {/* Page Navigation */}
            {totalPages > 1 && (
              <div className="flex items-center gap-1 bg-[#f8f5fc] rounded-lg p-1 ml-2">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage <= 1}
                  className="p-1.5 rounded hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4 text-[#6b6b6b]" />
                </button>
                <span className="px-2 text-xs font-medium text-[#6b6b6b]">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={currentPage >= totalPages}
                  className="p-1.5 rounded hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4 text-[#6b6b6b]" />
                </button>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#6b6b6b] bg-white border border-[#e8e0f0] rounded-lg hover:bg-[#f8f5fc] transition-colors"
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
            <button
              onClick={handleDownload}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#6b6b6b] bg-white border border-[#e8e0f0] rounded-lg hover:bg-[#f8f5fc] transition-colors"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
            <button
              onClick={onShare}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-[#704aaf] rounded-lg hover:bg-[#764BB7] transition-colors"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
          </div>
        </div>

        {/* Document Preview Area */}
        <div className="flex-1 overflow-auto bg-[#f5f5f5] p-6">
          <div 
            className="mx-auto bg-white shadow-lg rounded-lg overflow-hidden transition-all duration-200"
            style={{ 
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              maxWidth: '800px',
            }}
          >
            {/* Mock Document Content */}
            <div className="p-8">
              {/* Document Header */}
              <div className="text-center mb-8 pb-6 border-b border-[#e8e0f0]">
                <h1 className="text-2xl font-bold text-[#2d2d2d] mb-2">{document.name}</h1>
                <p className="text-sm text-[#6b6b6b]">{document.category}</p>
              </div>

              {/* Document Body - Mock Content */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-[#2d2d2d] mb-3">Treatment Details</h3>
                  <div className="space-y-2 text-sm text-[#5c5c5c]">
                    <p>This document confirms the consent and understanding of the treatment procedures outlined below. Please read carefully before signing.</p>
                    <p className="mt-4">The following treatment(s) have been discussed and agreed upon:</p>
                    <ul className="list-disc pl-5 mt-2 space-y-1">
                      <li>Treatment procedure and expected outcomes</li>
                      <li>Potential risks and side effects</li>
                      <li>Post-treatment care instructions</li>
                      <li>Follow-up appointment requirements</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-[#2d2d2d] mb-3">Client Acknowledgment</h3>
                  <div className="space-y-2 text-sm text-[#5c5c5c]">
                    <p>By signing this document, I acknowledge that:</p>
                    <ul className="list-disc pl-5 mt-2 space-y-1">
                      <li>I have been fully informed about the treatment</li>
                      <li>I have had the opportunity to ask questions</li>
                      <li>I understand the risks involved</li>
                      <li>I consent to the treatment</li>
                    </ul>
                  </div>
                </div>

                {/* Signature Section */}
                <div className="mt-8 pt-6 border-t border-[#e8e0f0]">
                  <div className="grid grid-cols-2 gap-8">
                    <div>
                      <p className="text-sm font-medium text-[#2d2d2d] mb-2">Client Signature</p>
                      <div className="h-16 border-b-2 border-[#e8e0f0] mb-2">
                        {document.status === "Signed" && (
                          <p className="italic text-[#704aaf] text-lg font-script">Sarah Miller</p>
                        )}
                      </div>
                      <p className="text-xs text-[#6b6b6b]">
                        {document.signedBy || "Client Name"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#2d2d2d] mb-2">Date</p>
                      <div className="h-16 border-b-2 border-[#e8e0f0] flex items-end pb-1 mb-2">
                        {document.status === "Signed" && (
                          <p className="text-[#5c5c5c]">{document.signedDate || document.dateUploaded}</p>
                        )}
                      </div>
                      <p className="text-xs text-[#6b6b6b]">Date Signed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer with Document Info */}
        <div className="px-5 py-3 border-t border-[#f0ebf5] bg-[#faf8fc]">
          <div className="flex items-center justify-between text-xs text-[#6b6b6b]">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                Uploaded: {document.dateUploaded}
              </span>
              {document.signedDate && (
                <span className="flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5" />
                  Signed: {document.signedDate}
                </span>
              )}
            </div>
            <span>Document ID: {document.id}</span>
          </div>
        </div>
      </div>
    </div>
  );
}


